import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  Users, Package, TrendingUp, TrendingDown, Calendar, RefreshCw,
  ArrowUpRight, ArrowDownRight, Zap, UserCheck, UserX, UserPlus,
  Filter, BarChart2,
} from 'lucide-react';
import { format, parseISO, isValid, subDays } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt  = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtShort = (n) => {
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000)   return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${Math.round(n || 0)}`;
};
const safeDate = (d) => { try { const p = parseISO(d); return isValid(p) ? format(p, 'dd MMM yy') : d; } catch { return d; } };

function PctBadge({ value }) {
  const up = value >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-bold ${up ? 'text-emerald-600' : 'text-red-500'}`}>
      {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      {Math.abs(value)}%
    </span>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="w-8 h-8 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin" />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 1 — CUSTOMER DASHBOARD
// ─────────────────────────────────────────────────────────────────────────────
const SEGMENT_CONFIG = [
  { key: 'active',   label: 'Active',          sub: 'Customers', icon: UserCheck, border: 'border-emerald-400', num: 'text-emerald-600', bg: 'bg-emerald-50' },
  { key: 'inactive', label: 'Inactive',         sub: 'Customers', icon: UserX,     border: 'border-red-400',     num: 'text-red-500',     bg: 'bg-red-50' },
  { key: 'new',      label: 'New',              sub: 'Customers', icon: UserPlus,  border: 'border-blue-400',    num: 'text-blue-600',    bg: 'bg-blue-50' },
  { key: 'existing', label: 'Existing',         sub: 'Customers', icon: Users,     border: 'border-violet-400',  num: 'text-violet-600',  bg: 'bg-violet-50' },
  { key: 'foldpass', label: 'FoldPass Members', sub: 'Active Plans',icon: Zap,    border: 'border-orange-400',  num: 'text-orange-600',  bg: 'bg-orange-50' },
];

const CUST_PERIODS = [
  { label: 'Last Week',     value: 'last_week' },
  { label: 'Last 2 Weeks',  value: 'last_2_weeks' },
  { label: 'Last Month',    value: 'last_month' },
  { label: 'Last 3 Months', value: 'last_3_months' },
  { label: 'Last 6 Months', value: 'last_6_months' },
  { label: 'All Time',      value: 'all_time' },
];

function CustomerDashboardTab({ token }) {
  const [period, setPeriod] = useState('last_month');
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/individual-customer-segments`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { period: p },
      });
      setData(res.data);
    } catch { toast.error('Failed to load customer data'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(period); }, [period, load]);

  const customers = selected && data?.[selected]?.customers || [];
  const cfg = SEGMENT_CONFIG.find(c => c.key === selected);

  return (
    <div className="space-y-5">
      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        <Filter className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <select value={period} onChange={e => { setPeriod(e.target.value); setSelected(null); }}
          className="text-sm font-semibold border border-slate-200 rounded-xl px-3 py-2 bg-white text-slate-700 focus:outline-none focus:border-blue-400">
          {CUST_PERIODS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <span className="text-xs text-slate-400 italic">Click a card to drill into customer details</span>
      </div>

      {loading ? <Spinner /> : !data ? null : (
        <>
          {/* Segment cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {SEGMENT_CONFIG.map(cfg => {
              const Icon = cfg.icon;
              const count = data[cfg.key]?.count ?? 0;
              const active = selected === cfg.key;
              return (
                <button key={cfg.key} onClick={() => setSelected(active ? null : cfg.key)}
                  className={`text-left p-5 rounded-2xl border-2 transition-all duration-200 hover:-translate-y-1 hover:shadow-md focus:outline-none ${
                    active ? `${cfg.bg} ${cfg.border} shadow-md -translate-y-1` : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Icon className={`w-6 h-6 mb-3 ${active ? cfg.num : 'text-slate-300'}`} />
                  <div className={`text-4xl font-black mb-1 ${active ? cfg.num : 'text-slate-800'}`}>{count}</div>
                  <div className={`text-[11px] font-black uppercase tracking-wider ${active ? cfg.num : 'text-slate-400'}`}>{cfg.label}</div>
                  <div className={`text-[10px] uppercase tracking-wider ${active ? cfg.num + ' opacity-70' : 'text-slate-300'}`}>{cfg.sub}</div>
                </button>
              );
            })}
          </div>

          {/* Total pill */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400 uppercase font-semibold tracking-wide">Total Registered</span>
            <span className="px-3 py-1 bg-slate-100 text-slate-700 text-sm font-black rounded-full">{data.total}</span>
          </div>

          {/* Drill-down table */}
          {selected && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className={`flex items-center justify-between px-5 py-4 border-b ${cfg.bg} border-slate-100`}>
                <div>
                  <h3 className={`font-black text-base ${cfg.num}`}>{cfg.label} {cfg.sub}</h3>
                  <p className="text-xs text-slate-400 mt-0.5">{customers.length} record{customers.length !== 1 ? 's' : ''} shown</p>
                </div>
                <button onClick={() => setSelected(null)}
                  className="text-xs text-slate-500 hover:text-slate-700 bg-white border border-slate-200 px-3 py-1.5 rounded-xl font-semibold">
                  Close ✕
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[700px]">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      {['Customer', 'Phone', 'Email', 'City', 'Registered', 'Orders', 'Total Spent', 'FoldPass'].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {customers.length === 0 ? (
                      <tr><td colSpan={8} className="text-center py-12 text-slate-400 text-sm">No customers in this segment for selected period</td></tr>
                    ) : customers.map((c, i) => (
                      <tr key={c.id || i} className={`border-b border-slate-50 hover:bg-slate-50 transition-colors ${i % 2 === 0 ? '' : 'bg-slate-50/40'}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-xl ${cfg.bg} flex items-center justify-center text-xs font-black flex-shrink-0 ${cfg.num}`}>
                              {(c.full_name || '?').charAt(0).toUpperCase()}
                            </div>
                            <span className="font-semibold text-slate-800 whitespace-nowrap">{c.full_name || '—'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap font-mono text-xs">{c.phone || '—'}</td>
                        <td className="px-4 py-3 text-slate-500 max-w-[160px] truncate text-xs">{c.email || '—'}</td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{c.city || '—'}</td>
                        <td className="px-4 py-3 text-slate-400 whitespace-nowrap text-xs">{c.created_at ? safeDate(c.created_at) : '—'}</td>
                        <td className="px-4 py-3 text-center font-black text-blue-600">{c.order_count || 0}</td>
                        <td className="px-4 py-3 font-bold text-emerald-600 whitespace-nowrap">{fmt(c.total_spent)}</td>
                        <td className="px-4 py-3">
                          {c.has_active_plan
                            ? <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-[10px] font-black rounded-full uppercase">Active</span>
                            : <span className="text-slate-300 text-xs">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 2 — DAILY SALES
// ─────────────────────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  pending:          'bg-amber-100 text-amber-700',
  pickup:           'bg-blue-100 text-blue-700',
  processed:        'bg-orange-100 text-orange-700',
  delivered:        'bg-emerald-100 text-emerald-700',
  delivery_overdue: 'bg-red-100 text-red-700',
};

function DailySalesTab({ token }) {
  const today = format(new Date(), 'yyyy-MM-dd');
  const ago30 = format(subDays(new Date(), 29), 'yyyy-MM-dd');
  const [start, setStart]         = useState(ago30);
  const [end, setEnd]             = useState(today);
  const [data, setData]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [chartY, setChartY]       = useState('orders');
  const [drillDate, setDrillDate] = useState(null);
  const [drillOrders, setDrillOrders]   = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const drillRef = useRef(null);

  const load = useCallback(async (s, e) => {
    setLoading(true);
    setDrillDate(null);
    try {
      const res = await axios.get(`${API}/admin/individual-daily-sales`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { start_date: s, end_date: e },
      });
      setData(res.data);
    } catch { toast.error('Failed to load daily sales'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(start, end); }, []);

  const handleDateClick = useCallback(async (date) => {
    if (drillDate === date) { setDrillDate(null); return; }
    setDrillDate(date);
    setDrillLoading(true);
    try {
      const res = await axios.get(`${API}/admin/individual-orders-by-date`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date },
      });
      setDrillOrders(res.data);
      // Scroll to drill-down panel
      setTimeout(() => drillRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch { toast.error('Failed to load orders'); }
    finally { setDrillLoading(false); }
  }, [token, drillDate]);

  const totals = data.reduce((a, r) => ({ orders: a.orders + r.orders, pieces: a.pieces + r.pieces, sales: a.sales + r.sales }), { orders: 0, pieces: 0, sales: 0 });
  const chartData = data.map(r => ({ ...r, label: r.date ? format(parseISO(r.date), 'd MMM') : r.date }));

  return (
    <div className="space-y-5">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <input type="date" value={start} onChange={e => setStart(e.target.value)}
          className="text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:border-blue-400" />
        <span className="text-slate-400 font-bold">→</span>
        <input type="date" value={end} onChange={e => setEnd(e.target.value)}
          className="text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:border-blue-400" />
        <button onClick={() => load(start, end)}
          className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Apply
        </button>
        <div className="ml-auto flex border border-slate-200 rounded-xl overflow-hidden text-sm font-semibold">
          {[{k:'orders',l:'Orders'},{k:'sales',l:'Sales'}].map(m => (
            <button key={m.k} onClick={() => setChartY(m.k)}
              className={`px-4 py-2 transition-colors ${chartY === m.k ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
              {m.l}
            </button>
          ))}
        </div>
      </div>

      {loading ? <Spinner /> : (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Total Orders', val: totals.orders, color: 'text-blue-600',    bg: 'bg-blue-50',    money: false },
              { label: 'Total Pieces', val: totals.pieces, color: 'text-violet-600',  bg: 'bg-violet-50',  money: false },
              { label: 'Total Sales',  val: totals.sales,  color: 'text-emerald-600', bg: 'bg-emerald-50', money: true  },
            ].map(k => (
              <div key={k.label} className={`${k.bg} rounded-2xl p-4`}>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{k.label}</p>
                <p className={`text-2xl font-black ${k.color}`}>{k.money ? fmt(k.val) : k.val.toLocaleString('en-IN')}</p>
              </div>
            ))}
          </div>

          {/* Bar chart */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <h3 className="font-black text-slate-800 mb-4">{chartY === 'orders' ? 'Orders' : 'Sales'} per Day</h3>
            {chartData.length === 0 ? (
              <div className="flex items-center justify-center h-40 text-slate-400 text-sm">No data for this range</div>
            ) : (
              <ResponsiveContainer width="100%" height={230}>
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={42}
                    tickFormatter={chartY === 'sales' ? fmtShort : undefined} />
                  <Tooltip formatter={(v) => chartY === 'sales' ? [fmt(v), 'Sales'] : [v, 'Orders']}
                    contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }} />
                  <Bar dataKey={chartY} fill="#2563EB" radius={[5, 5, 0, 0]}
                    name={chartY === 'orders' ? 'Orders' : 'Sales ₹'} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Daily Breakdown table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-black text-slate-800">Daily Breakdown</h3>
                <p className="text-xs text-slate-400 mt-0.5">Click any row to see order details for that day</p>
              </div>
              {drillDate && (
                <button onClick={() => setDrillDate(null)}
                  className="text-xs text-slate-500 hover:text-slate-700 bg-slate-100 px-3 py-1.5 rounded-xl font-semibold">
                  Clear ✕
                </button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    {['Date','Orders','Pieces','Sales',''].map((h, i) => (
                      <th key={i} className="text-left px-5 py-3 text-[10px] font-black text-slate-400 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-10 text-slate-400">No data for this date range</td></tr>
                  ) : data.map((row, i) => {
                    const isSelected = drillDate === row.date;
                    return (
                      <tr key={row.date}
                        onClick={() => handleDateClick(row.date)}
                        className={`border-b border-slate-50 cursor-pointer transition-colors ${
                          isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : `hover:bg-blue-50/40 ${i % 2 !== 0 ? 'bg-slate-50/40' : ''}`
                        }`}
                      >
                        <td className={`px-5 py-3 font-semibold ${isSelected ? 'text-blue-700' : 'text-slate-700'}`}>
                          {row.date ? format(parseISO(row.date), 'dd MMM yyyy') : row.date}
                        </td>
                        <td className={`px-5 py-3 font-black ${isSelected ? 'text-blue-600' : 'text-blue-500'}`}>{row.orders}</td>
                        <td className="px-5 py-3 font-bold text-violet-600">{row.pieces}</td>
                        <td className="px-5 py-3 font-bold text-emerald-600">{fmt(row.sales)}</td>
                        <td className="px-5 py-3 text-right">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-lg transition-colors ${
                            isSelected ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-blue-600'
                          }`}>
                            {isSelected ? 'Hide ▲' : 'Details ▼'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {data.length > 0 && (
                    <tr className="bg-slate-800 text-white">
                      <td className="px-5 py-3 font-black text-xs uppercase tracking-wider">Total</td>
                      <td className="px-5 py-3 font-black">{totals.orders}</td>
                      <td className="px-5 py-3 font-black">{totals.pieces}</td>
                      <td className="px-5 py-3 font-black">{fmt(totals.sales)}</td>
                      <td />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Drill-down panel */}
          {drillDate && (
            <div ref={drillRef} className="bg-white rounded-2xl border-2 border-blue-200 shadow-md overflow-hidden">
              <div className="px-5 py-4 border-b border-blue-100 bg-blue-50 flex items-center justify-between">
                <div>
                  <h3 className="font-black text-blue-800">
                    Orders on {format(parseISO(drillDate), 'dd MMMM yyyy')}
                  </h3>
                  <p className="text-xs text-blue-500 mt-0.5">
                    {drillLoading ? 'Loading...' : `${drillOrders.length} order${drillOrders.length !== 1 ? 's' : ''}`}
                  </p>
                </div>
                <button onClick={() => setDrillDate(null)}
                  className="text-xs text-blue-600 hover:text-blue-800 bg-white border border-blue-200 px-3 py-1.5 rounded-xl font-semibold">
                  Close ✕
                </button>
              </div>

              {drillLoading ? <Spinner /> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[800px]">
                    <thead className="bg-slate-50 border-b border-slate-100">
                      <tr>
                        {['#','Customer','Phone','Service','Clothes','Amount','Status','Payment','Placed At'].map(h => (
                          <th key={h} className="text-left px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {drillOrders.length === 0 ? (
                        <tr><td colSpan={9} className="text-center py-10 text-slate-400">No orders found for this date</td></tr>
                      ) : drillOrders.map((o, i) => {
                        const services = [...new Set((o.cart_items || []).map(c => c.category).filter(Boolean))];
                        const serviceLabel = services.length === 0 ? (o.service_summary || '—')
                          : services.length <= 2 ? services.join(', ')
                          : 'Mixed';
                        const sc = STATUS_COLORS[o.status] || 'bg-slate-100 text-slate-500';
                        const paid = o.payment_status === 'paid';
                        return (
                          <tr key={o.id || i} className={`border-b border-slate-50 hover:bg-blue-50/30 transition-colors ${i % 2 !== 0 ? 'bg-slate-50/30' : ''}`}>
                            <td className="px-4 py-3 text-slate-400 text-xs font-mono">{i + 1}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center text-white text-xs font-black flex-shrink-0">
                                  {(o.customer_name || '?').charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <p className="font-semibold text-slate-800 whitespace-nowrap">{o.customer_name || '—'}</p>
                                  <p className="text-[10px] text-slate-400">{o.invoice_number || o.id?.slice(0, 8)}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 font-mono text-xs text-slate-500">{o.customer_phone || '—'}</td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-0.5 bg-violet-50 text-violet-700 text-[10px] font-semibold rounded-full capitalize">
                                {serviceLabel}
                              </span>
                            </td>
                            <td className="px-4 py-3 font-bold text-slate-700 text-center">{o.total_clothes ?? '—'}</td>
                            <td className="px-4 py-3 font-black text-emerald-600 whitespace-nowrap">{fmt(o.total_amount)}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-black capitalize ${sc}`}>{o.status}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${paid ? 'bg-emerald-100 text-emerald-700' : 'bg-red-50 text-red-500'}`}>
                                {paid ? 'Paid' : 'Unpaid'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">
                              {o.order_date ? format(parseISO(o.order_date), 'hh:mm a') : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 3 — INSIGHTS (two independent panels)
// ─────────────────────────────────────────────────────────────────────────────
const METRICS = [
  { value: 'orders',           label: 'Orders' },
  { value: 'revenue',          label: 'Revenue' },
  { value: 'pickups',          label: 'Pickups' },
  { value: 'deliveries',       label: 'Deliveries' },
  { value: 'top_customers',    label: 'Top Customers' },
  { value: 'top_selling_items',label: 'Top Selling Items' },
];
const INSIGHT_PERIODS = [
  { value: 'last_7_days',   label: 'Last 7 Days' },
  { value: 'last_14_days',  label: 'Last 14 Days' },
  { value: 'last_30_days',  label: 'Last 30 Days' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_6_months', label: 'Last 6 Months' },
];

const COL_DEFS = {
  orders:           ['Date', 'Customer', 'Pieces', 'Sales', 'Status'],
  revenue:          ['Item / Service', 'Pieces', 'Total'],
  pickups:          ['Date', 'Customer', 'Pieces', 'Total'],
  deliveries:       ['Date', 'Customer', 'Pieces', 'Total'],
  top_customers:    ['Customer', 'Phone', 'Orders', 'Pieces', 'Total Spent'],
  top_selling_items:['Item', 'Pieces', 'Total'],
};

function InsightRow({ row, metric, i }) {
  const alt = i % 2 !== 0 ? 'bg-slate-50/40' : '';
  const c = `px-4 py-3 border-b border-slate-50 ${alt}`;
  const statusColor = { pending: 'amber', pickup: 'blue', processed: 'orange', delivered: 'emerald', delivery_overdue: 'red' };
  const sc = statusColor[row.status] || 'slate';

  switch (metric) {
    case 'orders':
      return (
        <tr className={`hover:bg-blue-50/20 transition-colors ${alt}`}>
          <td className={c + ' text-slate-500 whitespace-nowrap text-xs'}>{safeDate(row.date)}</td>
          <td className={c + ' font-semibold text-slate-700'}>{row.customer || '—'}</td>
          <td className={c + ' font-bold text-violet-600'}>{row.pieces ?? 0}</td>
          <td className={c + ' font-bold text-emerald-600 whitespace-nowrap'}>{fmt(row.total)}</td>
          <td className={c}>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-black bg-${sc}-100 text-${sc}-700 capitalize`}>{row.status}</span>
          </td>
        </tr>
      );
    case 'revenue':
    case 'top_selling_items':
      return (
        <tr className={`hover:bg-blue-50/20 transition-colors ${alt}`}>
          <td className={c + ' font-semibold text-slate-700'}>{row.item || '—'}</td>
          <td className={c + ' font-bold text-violet-600'}>{typeof row.pieces === 'number' ? row.pieces.toLocaleString('en-IN', {maximumFractionDigits: 2}) : row.pieces}</td>
          <td className={c + ' font-bold text-emerald-600 whitespace-nowrap'}>{fmt(row.total)}</td>
        </tr>
      );
    case 'pickups':
    case 'deliveries':
      return (
        <tr className={`hover:bg-blue-50/20 transition-colors ${alt}`}>
          <td className={c + ' text-slate-500 whitespace-nowrap text-xs'}>{safeDate(row.date)}</td>
          <td className={c + ' font-semibold text-slate-700'}>{row.customer || '—'}</td>
          <td className={c + ' font-bold text-violet-600'}>{row.pieces ?? 0}</td>
          <td className={c + ' font-bold text-emerald-600 whitespace-nowrap'}>{fmt(row.total)}</td>
        </tr>
      );
    case 'top_customers':
      return (
        <tr className={`hover:bg-blue-50/20 transition-colors ${alt}`}>
          <td className={c}>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center text-white text-xs font-black flex-shrink-0">
                {(row.item || '?').charAt(0).toUpperCase()}
              </div>
              <span className="font-semibold text-slate-700">{row.item}</span>
            </div>
          </td>
          <td className={c + ' text-slate-500 font-mono text-xs'}>{row.phone}</td>
          <td className={c + ' font-black text-blue-600 text-center'}>{row.orders}</td>
          <td className={c + ' font-bold text-violet-600'}>{row.pieces}</td>
          <td className={c + ' font-bold text-emerald-600 whitespace-nowrap'}>{fmt(row.total)}</td>
        </tr>
      );
    default: return null;
  }
}

function InsightsPanel({ token, defaultMetric, showChart }) {
  const [metric, setMetric]   = useState(defaultMetric);
  const [period, setPeriod]   = useState('last_30_days');
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (m, p) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/individual-insights`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { metric: m, period: p },
      });
      setData(res.data);
    } catch { toast.error('Failed to load insights'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(metric, period); }, [metric, period, load]);

  const rows = data?.rows || [];
  const columns = COL_DEFS[metric] || [];
  const canChart = showChart && ['orders','revenue','pickups','deliveries'].includes(metric);

  // Build simple chart from date rows
  const chartData = canChart
    ? rows.slice(0, 60).map(r => ({
        label: r.date ? format(parseISO(r.date), 'd MMM') : (r.item || ''),
        value: metric === 'revenue' ? (r.total || 0) : 1,
      }))
    : [];

  // For orders: aggregate by date
  const aggChart = canChart && metric === 'orders'
    ? Object.values(rows.reduce((acc, r) => {
        const key = r.date || '';
        if (!acc[key]) acc[key] = { label: r.date ? format(parseISO(r.date), 'd MMM') : key, value: 0 };
        acc[key].value += 1;
        return acc;
      }, {}))
    : chartData;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/60">
        <div className="flex flex-wrap items-center gap-2">
          <select value={metric} onChange={e => setMetric(e.target.value)}
            className="text-sm font-semibold border border-slate-200 rounded-xl px-3 py-2 bg-white text-slate-700 focus:outline-none focus:border-blue-400">
            {METRICS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <span className="text-slate-400 font-medium text-sm">of</span>
          <select value={period} onChange={e => setPeriod(e.target.value)}
            className="text-sm font-semibold border border-slate-200 rounded-xl px-3 py-2 bg-white text-slate-700 focus:outline-none focus:border-blue-400">
            {INSIGHT_PERIODS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {loading ? <Spinner /> : (
        <>
          {/* Optional chart */}
          {canChart && aggChart.length > 0 && (
            <div className="px-5 pt-4 pb-2">
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={aggChart} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={36}
                    tickFormatter={metric === 'revenue' ? fmtShort : undefined} />
                  <Tooltip formatter={(v) => metric === 'revenue' ? [fmt(v), 'Revenue'] : [v, metric]}
                    contentStyle={{ borderRadius: 10, fontSize: 11 }} />
                  <Bar dataKey="value" fill="#2563EB" radius={[4, 4, 0, 0]}
                    name={metric === 'revenue' ? 'Revenue' : metric.charAt(0).toUpperCase() + metric.slice(1)} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Table */}
          <div className="overflow-auto flex-1">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100 sticky top-0">
                <tr>
                  {columns.map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0
                  ? <tr><td colSpan={columns.length} className="text-center py-12 text-slate-400 text-sm">No data for this selection</td></tr>
                  : rows.slice(0, 200).map((row, i) => <InsightRow key={i} row={row} metric={metric} i={i} />)
                }
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function InsightsTab({ token }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" style={{ minHeight: 560 }}>
      <InsightsPanel token={token} defaultMetric="orders"            showChart={true} />
      <InsightsPanel token={token} defaultMetric="top_selling_items" showChart={false} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 4 — COMPARISON
// ─────────────────────────────────────────────────────────────────────────────
const COMP_MODES = [
  { value: 'month',     label: 'This Month vs Last Month' },
  { value: 'quarter',   label: 'This Quarter vs Last Quarter' },
  { value: 'half_year', label: 'This Half Year vs Last Half Year' },
  { value: 'year',      label: 'This Year vs Last Year' },
];

function CompCard({ title, icon: Icon, color, curr, prev, pct, sub1, sub2, money }) {
  const palettes = {
    pink:  { wrap: 'bg-pink-50 border-pink-200',    h: 'text-pink-700',    v: 'text-pink-800' },
    green: { wrap: 'bg-emerald-50 border-emerald-200', h: 'text-emerald-700', v: 'text-emerald-800' },
    blue:  { wrap: 'bg-blue-50 border-blue-200',    h: 'text-blue-700',    v: 'text-blue-800' },
  };
  const p = palettes[color] || palettes.blue;
  const disp = (n) => money ? fmt(n) : (n ?? 0).toLocaleString('en-IN');

  return (
    <div className={`border-2 rounded-2xl p-5 ${p.wrap}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-5 h-5 ${p.h}`} />
        <span className={`font-black text-xs uppercase tracking-widest ${p.h}`}>{title}</span>
      </div>
      <div className={`text-4xl font-black mb-1 ${p.v}`}>{disp(curr)}</div>
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs text-slate-500">vs {disp(prev)}</span>
        <PctBadge value={pct} />
      </div>
      {(sub1 || sub2) && (
        <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-200/60">
          {[sub1, sub2].filter(Boolean).map((s, i) => (
            <div key={i}>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">{s.label}</p>
              <p className={`text-xl font-black ${p.v}`}>{s.money ? fmt(s.curr) : (s.curr ?? 0).toLocaleString('en-IN')}</p>
              <PctBadge value={s.pct ?? 0} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ComparisonTab({ token }) {
  const [mode, setMode]   = useState('month');
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/individual-comparison`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { mode: m },
      });
      setData(res.data);
    } catch { toast.error('Failed to load comparison'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(mode); }, [mode, load]);

  const curr    = data?.current  || {};
  const prev    = data?.previous || {};
  const changes = data?.changes  || {};
  const labels  = data?.labels   || { curr: 'This', prev: 'Last' };
  const chart   = data?.chart_data || [];

  const tooltipStyle = { borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 };

  return (
    <div className="space-y-5">
      {/* Mode selector */}
      <div className="flex items-center gap-3 flex-wrap">
        <Filter className="w-4 h-4 text-slate-400" />
        <select value={mode} onChange={e => setMode(e.target.value)}
          className="text-sm font-semibold border border-slate-200 rounded-xl px-4 py-2 bg-white text-slate-700 focus:outline-none focus:border-blue-400">
          {COMP_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </div>

      {loading ? <Spinner /> : !data ? null : (
        <>
          {/* Comparison KPI cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <CompCard
              title="Orders" icon={Package} color="pink"
              curr={curr.orders} prev={prev.orders} pct={changes.orders}
              sub1={{ label: 'Pickups',    curr: curr.pickups,    pct: changes.pickups }}
              sub2={{ label: 'Deliveries', curr: curr.deliveries, pct: changes.deliveries }}
            />
            <CompCard
              title="Revenue" icon={TrendingUp} color="green" money
              curr={curr.sales} prev={prev.sales} pct={changes.sales}
              sub1={{ label: 'Avg / Order', curr: curr.orders ? Math.round(curr.sales / curr.orders) : 0, pct: 0, money: true }}
              sub2={{ label: 'Paid Orders', curr: curr.orders, pct: 0 }}
            />
            <CompCard
              title="New Customers" icon={Users} color="blue"
              curr={curr.new_customers} prev={prev.new_customers} pct={changes.new_customers}
              sub1={{ label: 'Active Total',   curr: curr.active,   pct: 0 }}
              sub2={{ label: 'Inactive Total', curr: curr.inactive, pct: 0 }}
            />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Revenue comparison — grouped bar */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <h3 className="font-black text-slate-800 flex-1">Revenue Comparison</h3>
                <div className="flex items-center gap-3 text-xs">
                  <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-rose-400 inline-block" />{labels.prev}</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-blue-500 inline-block" />{labels.curr}</span>
                </div>
              </div>
              {chart.length === 0 ? (
                <div className="flex items-center justify-center h-44 text-slate-400 text-sm">No data</div>
              ) : (
                <ResponsiveContainer width="100%" height={230}>
                  <BarChart data={chart} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barCategoryGap="30%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="x" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={46} tickFormatter={fmtShort} />
                    <Tooltip formatter={(v, n) => [fmt(v), n]} contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="prev_sales" name={labels.prev} fill="#f87171" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="curr_sales" name={labels.curr} fill="#3b82f6" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Orders comparison — line chart */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <h3 className="font-black text-slate-800 flex-1">Orders Comparison</h3>
                <div className="flex items-center gap-3 text-xs">
                  <span className="flex items-center gap-1.5"><span className="w-5 border-t-2 border-rose-400 inline-block" />{labels.prev}</span>
                  <span className="flex items-center gap-1.5"><span className="w-5 border-t-2 border-blue-500 inline-block" />{labels.curr}</span>
                </div>
              </div>
              {chart.length === 0 ? (
                <div className="flex items-center justify-center h-44 text-slate-400 text-sm">No data</div>
              ) : (
                <ResponsiveContainer width="100%" height={230}>
                  <LineChart data={chart} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="x" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={28} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="prev_orders" name={labels.prev} stroke="#f87171" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="curr_orders" name={labels.curr} stroke="#3b82f6" strokeWidth={2.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN — Tab shell
// ─────────────────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'customers', label: 'Customer Dashboard', icon: Users },
  { key: 'daily',     label: 'Daily Sales',        icon: Package },
  { key: 'insights',  label: 'Insights',           icon: BarChart2 },
  { key: 'comparison',label: 'Comparison',         icon: TrendingUp },
];

export default function AdminIndividualAnalytics() {
  const { token } = useAuth();
  const [tab, setTab] = useState('customers');

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-black text-slate-800">Individual Customer Dashboard</h1>
        <p className="text-sm text-slate-400 mt-0.5">Detailed insights for individual customer orders & revenue</p>
      </div>

      {/* Tab nav */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-2xl w-fit flex-wrap">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 whitespace-nowrap ${
                active ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}>
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {tab === 'customers'  && <CustomerDashboardTab token={token} />}
      {tab === 'daily'      && <DailySalesTab        token={token} />}
      {tab === 'insights'   && <InsightsTab          token={token} />}
      {tab === 'comparison' && <ComparisonTab        token={token} />}
    </div>
  );
}
